import React, { useEffect, useState } from 'react';
import {
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { Search as SearchIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../../components/common';
import { usePlanCuentaStore } from '../store/planCuentaStore';
import type { PlanCuenta } from '../types/planCuenta';
import { NATURALEZA_OPTIONS, TIPO_CUENTA_OPTIONS, initialPlanCuenta } from '../types/planCuenta';

const PlanCuentasPage: React.FC = () => {
  const {
    cuentas,
    loading,
    error,
    selectedCuenta,
    dialogOpen,
    fetchCuentas,
    saveCuenta,
    deleteCuenta,
    setSelectedCuenta,
    openDialog,
    closeDialog,
    clearError,
  } = usePlanCuentaStore();

  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    fetchCuentas();
  }, [fetchCuentas]);

  // Flatten hierarchical cuentas for DataGrid display
  const flattenCuentas = (items: PlanCuenta[]): PlanCuenta[] => {
    const result: PlanCuenta[] = [];
    const addItems = (list: PlanCuenta[]) => {
      for (const item of list) {
        result.push(item);
        if (item.cuentasHijas && item.cuentasHijas.length > 0) {
          addItems(item.cuentasHijas);
        }
      }
    };
    addItems(items);
    return result;
  };

  const flatCuentas = flattenCuentas(cuentas);

  const filteredCuentas = flatCuentas.filter(c =>
    !searchText ||
    c.codigoCuenta.toLowerCase().includes(searchText.toLowerCase()) ||
    c.descripcion.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns: GridColDef[] = [
    {
      field: 'codigoCuenta',
      headerName: 'Código',
      width: 150,
    },
    {
      field: 'descripcion',
      headerName: 'Descripción',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <span style={{ paddingLeft: `${(params.row.nivel - 1) * 24}px` }}>
          {params.value}
        </span>
      ),
    },
    {
      field: 'nivel',
      headerName: 'Nivel',
      width: 80,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'naturalezaCuenta',
      headerName: 'Naturaleza',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'D' ? 'Deudora' : 'Acreedora'}
          size="small"
          color={params.value === 'D' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'tipoCuenta',
      headerName: 'Tipo',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value === 'B' ? 'Balance' : 'Resultado'}
          size="small"
          color={params.value === 'B' ? 'info' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'esMovimiento',
      headerName: 'Movimiento',
      width: 110,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) =>
        params.value ? <Chip label="Sí" size="small" color="success" /> : <Chip label="No" size="small" />,
    },
    {
      field: 'activo',
      headerName: 'Estado',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
          <GridEditButton
            onClick={() => handleEdit(params.row)}
            ariaLabel={`Editar cuenta ${params.row.codigoCuenta}`}
          />
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.idPlanCuenta)}
            aria-label={`Eliminar cuenta ${params.row.codigoCuenta}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const handleEdit = (cuenta: PlanCuenta) => {
    setSelectedCuenta(cuenta);
    openDialog();
  };

  const handleCreate = () => {
    setSelectedCuenta(null);
    openDialog();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de eliminar esta cuenta?')) {
      try {
        await deleteCuenta(id);
      } catch (err) {
        // Error handled by store
      }
    }
  };

  const [formData, setFormData] = useState<Partial<PlanCuenta>>(initialPlanCuenta);

  useEffect(() => {
    if (dialogOpen) {
      setFormData(selectedCuenta || { ...initialPlanCuenta });
    }
  }, [dialogOpen, selectedCuenta]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      await saveCuenta(formData);
    } catch (err) {
      // Error handled by store
    }
  };

  // Get flat list of cuentas for parent selection (only cuentas with lower nivel)
  const parentOptions = flatCuentas.filter(c =>
    c.nivel < 4 && c.idPlanCuenta !== formData.idPlanCuenta
  );

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Plan de Cuentas"
        subtitle="Gestión del plan de cuentas contables"
        actions={
          <AddButton text="Nueva Cuenta" onClick={handleCreate} />
        }
      />

      <TextField
        size="small"
        placeholder="Buscar por código o descripción..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 2, maxWidth: 400 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {loading && <CircularProgress />}
      {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && (
        <DataGrid
          columns={columns}
          rows={filteredCuentas}
          getRowId={(row: PlanCuenta) => row.idPlanCuenta}
        />
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCuenta ? 'Editar Cuenta' : 'Nueva Cuenta'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Código de Cuenta"
                value={formData.codigoCuenta || ''}
                onChange={(e) => handleFormChange('codigoCuenta', e.target.value)}
                inputProps={{ maxLength: 12 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Descripción"
                value={formData.descripcion || ''}
                onChange={(e) => handleFormChange('descripcion', e.target.value)}
                inputProps={{ maxLength: 200 }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Nivel"
                type="number"
                value={formData.nivel || 1}
                onChange={(e) => handleFormChange('nivel', parseInt(e.target.value))}
                inputProps={{ min: 1, max: 4 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Naturaleza</InputLabel>
                <Select
                  value={formData.naturalezaCuenta || 'D'}
                  label="Naturaleza"
                  onChange={(e) => handleFormChange('naturalezaCuenta', e.target.value)}
                >
                  {NATURALEZA_OPTIONS.map(opt => (
                    <MuiMenuItem key={opt.value} value={opt.value}>{opt.label}</MuiMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Cuenta</InputLabel>
                <Select
                  value={formData.tipoCuenta || 'B'}
                  label="Tipo de Cuenta"
                  onChange={(e) => handleFormChange('tipoCuenta', e.target.value)}
                >
                  {TIPO_CUENTA_OPTIONS.map(opt => (
                    <MuiMenuItem key={opt.value} value={opt.value}>{opt.label}</MuiMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={parentOptions}
                getOptionLabel={(opt) => `${opt.codigoCuenta} - ${opt.descripcion}`}
                value={parentOptions.find(p => p.idPlanCuenta === formData.idPlanCuentaPadre) || null}
                onChange={(_e, newVal) => {
                  handleFormChange('idPlanCuentaPadre', newVal?.idPlanCuenta || null);
                  handleFormChange('codigoCuentaPadre', newVal?.codigoCuenta || null);
                }}
                renderInput={(params) => <TextField {...params} label="Cuenta Padre" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Moneda Control"
                value={formData.monedaControl || ''}
                onChange={(e) => handleFormChange('monedaControl', e.target.value || null)}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.esMovimiento || false}
                    onChange={(e) => handleFormChange('esMovimiento', e.target.checked)}
                  />
                }
                label="Es Cuenta de Movimiento"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.esEstadoFinanciero || false}
                    onChange={(e) => handleFormChange('esEstadoFinanciero', e.target.checked)}
                  />
                }
                label="Estado Financiero"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.activo !== false}
                    onChange={(e) => handleFormChange('activo', e.target.checked)}
                  />
                }
                label="Activo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </ListLayoutPanel>
  );
};

export default PlanCuentasPage;
